import { useEffect, useRef } from "react";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm.js";

import { cn } from "~/lib/utils";

const WAVEFORM_HEIGHT_PX = 20;
const WAVEFORM_WINDOW_SECONDS = 6;

export function VoiceWaveform(props: {
  source: MediaStream | Blob;
  theme: "light" | "dark";
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { source, theme } = props;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.dataset.theme = theme;
    const options = {
      container,
      height: WAVEFORM_HEIGHT_PX,
      waveColor: getComputedStyle(container).color,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      barMinHeight: 2,
      cursorWidth: 0,
      interact: false,
      hideScrollbar: true,
    };
    if (source instanceof Blob) {
      const wavesurfer = WaveSurfer.create({ ...options, normalize: true });
      wavesurfer.loadBlob(source).catch(() => undefined);
      return () => wavesurfer.destroy();
    }
    const record = RecordPlugin.create({
      scrollingWaveform: true,
      scrollingWaveformWindow: WAVEFORM_WINDOW_SECONDS,
      renderRecordedAudio: false,
    });
    const wavesurfer = WaveSurfer.create({ ...options, plugins: [record] });
    const micStream = record.renderMicStream(source);
    return () => {
      micStream.onDestroy();
      wavesurfer.destroy();
    };
  }, [source, theme]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className={cn("min-w-0 flex-1 text-muted-foreground", props.className)}
    />
  );
}
